import { daysBetween, formatDayKey } from '../../../function-utils/building-time';
import { countOf } from '../../../function-utils/plural';

/**
 * THE STAY PICKER'S RULES — pure, on building day keys.
 *
 * Ranges are HALF-OPEN: [start, end) where `end` is the CHECK-OUT day. The
 * nights are start … end − 1, so a stay's check-out day is a free check-in day
 * and its check-in day is a free check-out day (back-to-back stays). Day keys
 * compare as strings ('YYYY-MM-DD' sorts in date order) and nights are counted
 * by calendar arithmetic (daysBetween), so a clock change never makes a night
 * 23 or 25 hours long.
 *
 * Driven by src/utils/stays-range.cases.json, the copy of the backend's
 * scripts/stays/range-cases.json: `overlap` and `nights` are asserted on both
 * sides, `taps` and `conflicts` are this picker's own.
 */

export type OccupiedKind = 'stay' | 'owner_block' | 'provider_block';

export interface OccupiedRange {
  start: string;
  end: string;
  kind: OccupiedKind;
  label: string;
}

export interface DateRangeValue {
  start: string | null;
  /** The check-out day. */
  end: string | null;
}

export interface Span {
  start: string;
  end: string;
}

const EMPTY: DateRangeValue = { start: null, end: null };

/**
 * One tap (or click, or Enter) on a day.
 *   nothing chosen          → check-in
 *   check-in only, later    → check-out
 *   check-in only, on/before → check-in again, there
 *   both chosen             → start over, there
 */
export const activateDay = (value: DateRangeValue, day: string): DateRangeValue => {
  if (value.start && !value.end && day > value.start) {
    return { start: value.start, end: day };
  }

  return { start: day, end: null };
};

/** A drag from one day to another, in either direction. The later day is the
 *  check-out; a drag back to where it began is a check-in only. */
export const rangeFromDrag = (a: string, b: string): DateRangeValue => {
  if (a === b) return { start: a, end: null };

  return a < b ? { start: a, end: b } : { start: b, end: a };
};

/** Nights from check-in to check-out, by keys. 0 until both are set. */
export const nightsOf = (start: string | null, end: string | null): number => {
  if (!start || !end) return 0;
  const nights = daysBetween(start, end);

  return Number.isFinite(nights) && nights > 0 ? nights : 0;
};

/** Half-open overlap: touching ends do not overlap. */
export const rangesOverlap = (a: Span, b: Span): boolean =>
  a.start < b.end && b.start < a.end;

/**
 * What the chosen dates run into. Stays and owner blocks are CONFLICTS; a
 * channel's (provider's) unavailable nights only WARN. Nothing until both
 * check-in and check-out are chosen.
 */
export const rangeConflicts = (
  value: DateRangeValue,
  occupied: ReadonlyArray<OccupiedRange>
): { conflicts: OccupiedRange[]; warnings: OccupiedRange[] } => {
  const conflicts: OccupiedRange[] = [];
  const warnings: OccupiedRange[] = [];
  if (!value.start || !value.end || value.start >= value.end) {
    return { conflicts, warnings };
  }
  const chosen: Span = { start: value.start, end: value.end };
  for (const range of occupied) {
    if (!rangesOverlap(chosen, range)) continue;
    if (range.kind === 'provider_block') warnings.push(range);
    else conflicts.push(range);
  }

  return { conflicts, warnings };
};

const PRIORITY: Record<OccupiedKind, number> = {
  stay: 0,
  owner_block: 1,
  provider_block: 2,
};

/** The range that covers the NIGHT of `day` (start <= day < end), or null.
 *  A stay outranks a block, which outranks a channel's hold. */
export const markOf = (
  day: string,
  occupied: ReadonlyArray<OccupiedRange>
): OccupiedRange | null => {
  let found: OccupiedRange | null = null;
  for (const range of occupied) {
    if (range.start <= day && day < range.end) {
      if (!found || PRIORITY[range.kind] < PRIORITY[found.kind]) found = range;
    }
  }

  return found;
};

/** What a marked night says inside its day button's name. */
export const markText = (range: OccupiedRange): string => {
  switch (range.kind) {
    case 'stay':
      return `booked: ${range.label}`;
    case 'owner_block':
      return `closed: ${range.label}`;
    default:
      return `${range.label} unavailable`;
  }
};

const SHORT: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };

/** "14–17 Oct", "30 Oct–2 Nov", "30 Dec 2026–2 Jan 2027". */
export const spanText = (start: string, end: string): string => {
  if (start.slice(0, 4) !== end.slice(0, 4)) {
    const withYear: Intl.DateTimeFormatOptions = { ...SHORT, year: 'numeric' };

    return `${formatDayKey(start, withYear)}–${formatDayKey(end, withYear)}`;
  }
  if (start.slice(0, 7) === end.slice(0, 7)) {
    return `${Number(start.slice(8, 10))}–${formatDayKey(end, SHORT)}`;
  }

  return `${formatDayKey(start, SHORT)}–${formatDayKey(end, SHORT)}`;
};

/** The sentence a conflict (or a channel warning) is explained in. */
export const conflictText = (range: OccupiedRange): string => {
  switch (range.kind) {
    case 'stay':
      return `These dates overlap ${range.label}'s stay (${spanText(range.start, range.end)})`;
    case 'owner_block':
      return `These dates include closed nights (${range.label})`;
    default:
      return `${range.label} shows these nights as unavailable`;
  }
};

/** What the polite live region says after each step. */
export const liveText = (value: DateRangeValue = EMPTY): string => {
  if (!value.start) return 'Choose check-in.';
  if (!value.end) {
    return `Check-in ${formatDayKey(value.start, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })}. Choose check-out.`;
  }

  return `${countOf(nightsOf(value.start, value.end), 'night')}, ${formatDayKey(
    value.start,
    SHORT
  )} to ${formatDayKey(value.end, SHORT)}`;
};
